import type { Href } from "expo-router";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import { COLORS, FONTS } from "../../lib/brand";

import { AuthCard } from "../../components/auth/AuthCard";
import { Button, Input, ProgressBar } from "../../components/ui";
import { useAuth } from "../../lib/authContext";
import { supabase } from "../../lib/supabase";

const AMBER = COLORS.AMBER.hex;
const SUBTITLE = "rgba(28,43,26,0.68)";

function strengthFor(pw: string): number {
  // Same brackets as the previous implementation — keep the curve so users
  // who saw the old meter aren't suddenly told their password is weaker.
  if (pw.length >= 14) return 1;
  if (pw.length >= 11) return 0.8;
  if (pw.length >= 9) return 0.6;
  if (pw.length >= 8) return 0.4;
  if (pw.length > 0) return 0.2;
  return 0;
}

function meetsRequirements(pw: string): { ok: boolean; reason?: string } {
  if (pw.length < 8) return { ok: false, reason: "Use at least 8 characters." };
  if (!/[a-zA-Z]/.test(pw)) return { ok: false, reason: "Include at least one letter." };
  if (!/[0-9]/.test(pw)) return { ok: false, reason: "Include at least one number." };
  return { ok: true };
}

export default function ResetPasswordScreen() {
  const router = useRouter();
  const { user, loading } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => {
      router.replace("/login" as Href);
    }, 1500);
    return () => clearTimeout(t);
  }, [success, router]);

  async function onSubmit() {
    setError(null);
    const check = meetsRequirements(password);
    if (!check.ok) {
      setError(check.reason ?? "Password is too weak.");
      return;
    }
    if (password !== confirmPassword) {
      setError("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) {
        setError(updateErr.message);
        return;
      }
      setSuccess(true);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AuthCard>
        <Text className="text-center text-[14px]" style={{ fontFamily: FONTS.body, color: SUBTITLE }}>
          Loading…
        </Text>
      </AuthCard>
    );
  }

  // Supabase sets the session from the recovery token in the URL hash. If we
  // got here without a session, the link is either expired, already used, or
  // malformed.
  if (!user) {
    return (
      <AuthCard>
        <Text
          className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
          style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
        >
          LINK EXPIRED
        </Text>
        <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: FONTS.display }}>
          This link isn't valid.
        </Text>
        <Text className="mt-3 text-[14px] leading-6" style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}>
          Password reset links expire after a short window, or once they've been used.
          Request a fresh one and try again.
        </Text>

        <View className="mt-6">
          <Button variant="primary" onPress={() => router.replace("/forgot-password" as Href)}>
            Request a new link →
          </Button>
        </View>

        <Pressable
          onPress={() => router.replace("/login" as Href)}
          className="mt-4 self-center"
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
        >
          <Text className="text-center text-[12px]" style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}>
            ← Back to sign in
          </Text>
        </Pressable>
      </AuthCard>
    );
  }

  if (success) {
    return (
      <AuthCard>
        <Text
          className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
          style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
        >
          ALL SET
        </Text>
        <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: FONTS.display }}>
          Password updated.
        </Text>
        <Text className="mt-3 text-[14px] leading-6" style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}>
          You're being redirected to the sign-in page — use your new password to continue.
        </Text>
      </AuthCard>
    );
  }

  const strength = strengthFor(password);

  return (
    <AuthCard>
      <Text
        className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
        style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
      >
        SET A NEW PASSWORD
      </Text>
      <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: FONTS.display }}>
        Choose a fresh password.
      </Text>
      <Text className="mt-2 text-[14px] leading-6" style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}>
        Use at least 8 characters with a mix of letters and numbers.
      </Text>

      <View className="mt-6">
        <Input
          label="New password"
          value={password}
          onChangeText={setPassword}
          placeholder="••••••••"
          secureTextEntry
          autoComplete="new-password"
          textContentType="newPassword"
          returnKeyType="next"
        />
      </View>
      <View className="mt-2">
        <ProgressBar value={strength} size="sm" />
      </View>

      <View className="mt-4">
        <Input
          label="Confirm new password"
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          placeholder="••••••••"
          secureTextEntry
          autoComplete="new-password"
          textContentType="newPassword"
          returnKeyType="go"
          onSubmitEditing={() => void onSubmit()}
        />
      </View>

      {error ? (
        <Text
          className="mb-1 mt-3 text-center text-sm text-red-600"
          style={{ fontFamily: FONTS.body }}
        >
          {error}
        </Text>
      ) : null}

      <View className="mt-4">
        <Button variant="primary" loading={busy} onPress={() => void onSubmit()}>
          {busy ? "Updating password…" : "Update password"}
        </Button>
      </View>

      <Pressable
        onPress={() => router.replace("/login" as Href)}
        className="mt-4 self-center"
        style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
      >
        <Text className="text-center text-[12px]" style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}>
          ← Back to sign in
        </Text>
      </Pressable>

    </AuthCard>
  );
}
